import type { ChatInterfaceProps } from "../types";
import { ChatInput, mergeLabels, MessageList } from "./shared/components";
import { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "antd";
import {
  MinusSquareOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";

/** @ts-ignore */
import avatar from "url:../assets/cactai.png";
import { Tooltip } from "@alku/ui-kit";
import { cancelMessage } from "../services/ChatService";

const NAV_BAR_HEIGHT = 72;
const RESIZE_DEBOUNCE_MS = 200;

function dimensionsForWindow(minimized: boolean) {
  return {
    width: minimized ? window.innerWidth / 2 : window.innerWidth,
    height: minimized
      ? window.innerHeight * 0.8
      : window.innerHeight - NAV_BAR_HEIGHT - 5,
  };
}

/** Batch dimension updates to the next frame (reduces ResizeObserver churn). */
function scheduleDimensionUpdate(
  apply: (minimized: boolean) => void,
  minimized: boolean,
) {
  requestAnimationFrame(() => apply(minimized));
}

export function ChatInterface({
  messages,
  onSendMessage,
  isLoading = false,
  disabled = false,
  className,
  labels,
  renderMessage,
  autoFocusInput = true,
  maxInputLength,
  setIsLoading,
  isStandalone = false,
}: ChatInterfaceProps) {
  const merged = mergeLabels(labels);
  const rootClass = ["cactai-chat", className].filter(Boolean).join(" ");
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [minimized, setMinimized] = useState(true);
  const [height, setHeight] = useState(window.innerHeight);
  const [isStandaloneView, setIsStandaloneView] = useState(isStandalone);
  const minimizedRef = useRef(minimized);
  minimizedRef.current = minimized;

  const cancelPrompt = () => {
    cancelMessage();
    setIsLoading?.(false);
  };

  const [scrollPosition, setScrollPosition] = useState(0);
  const applyDimensions = useCallback((isMinimized: boolean) => {
    const { width: w, height: h } = dimensionsForWindow(isMinimized);
    setWidth(w);
    setHeight(h);
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const onResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        scheduleDimensionUpdate(applyDimensions, minimizedRef.current);
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", onResize);
    };
  }, [applyDimensions]);

  const showModal = () => {
    scheduleDimensionUpdate(applyDimensions, minimizedRef.current);
    setOpen(true);
    document.documentElement.style.setProperty('overflow-y', 'auto', 'important');
    document.body.style.setProperty('overflow-y', 'auto', 'important');
  };

  const maximize = () => {
    setMinimized(false);
    requestAnimationFrame(() => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight - NAV_BAR_HEIGHT - 5);
    });
  };

  const minimize = () => {
    setMinimized(true);
    scheduleDimensionUpdate(applyDimensions, true);
  };

  return (
    <>
    
      {isStandaloneView ? (
      <div className="chat-standalone-shell">
        <div className="chat-header-standalone">
          CACTAI Chat
        </div>
        <div className="chat-body-standalone">
          <MessageList
            messages={messages}
            emptyLabel={merged.emptyState}
            renderMessage={renderMessage}
            isLoading={isLoading}
          />
        </div>

        <div className="chat-footer-standalone">
          <ChatInput
            onSubmit={onSendMessage}
            disabled={disabled || isLoading}
            placeholder={merged.inputPlaceholder}
            sendLabel={merged.sendButton}
            autoFocus={autoFocusInput}
            maxLength={maxInputLength}
            onCancel={cancelPrompt}
          />
        </div>
      </div>
      ) : (
      <div className="chat-interface-container">
      <Modal
        style={minimized ? { position: 'absolute', bottom: '0px', right: '0px' }: {}}
        className="cactai-chat-modal"
        title={
          <div className="chat-header" onFocus={() => {}} onBlur={() => {}}>
            CactAI Chat
            <div className="button-container">
              {minimized ? (
                <Tooltip content="Maximize" id="maximize">
                  <FullscreenOutlined
                    className="chat-header-maximize"
                    onClick={maximize}
                  />
                </Tooltip>
              ) : (
                <Tooltip content="Minimize" id="minimize">
                  <FullscreenExitOutlined
                    className="chat-header-minimize"
                    onClick={minimize}
                  />
                </Tooltip>
              )}
              <Tooltip content="Close" id="close">
                <MinusSquareOutlined
                  className="chat-header-close"
                  onClick={() => setOpen(false)}
                />
              </Tooltip>
            </div>
          </div>
        }
        open={open}
        width={width}
        centered={false}
        closable={false}
        footer={null}
        mask={false}

        onCancel={() => setOpen(false)}
        zIndex={9999}
        styles={{
          wrapper: { pointerEvents: "none"},
          content: {
            height: `${height}px`,
            maxHeight: `${height}px`,
            pointerEvents: "auto",
          },
        }}
        modalRender={(modal) => <div className="cactai-chat-modal-shell">{modal}</div>}
      >
        <section className={rootClass} aria-label="Chat">
          <div className="chat-body">
            <MessageList
              messages={messages}
              emptyLabel={merged.emptyState}
              renderMessage={renderMessage}
              isLoading={isLoading}
            />
          </div>

          <footer className="chat-footer">
            <ChatInput
              onSubmit={onSendMessage}
              disabled={disabled || isLoading}
              placeholder={merged.inputPlaceholder}
              sendLabel={merged.sendButton}
              autoFocus={autoFocusInput}
              maxLength={maxInputLength}
              onCancel={cancelPrompt}
            />
          </footer>
        </section>
      </Modal>
      <div className="chat-avatar-button-container" onClick={() => open ? setOpen(false) : showModal()}>
        <img src={avatar} alt="CactAI" className="chat-avatar-button-image" />
      </div>
    </div>)}
  </>
)}
